import React from "react";
import AuthLink from "../../../../Auth/AuthLink";
import { ArrowRight, Calendar, Check, CheckCircle, ExternalLink, MapPin, User, Users } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn, decodeEntities, safeExternalUrl } from "@/lib/utils";
import { isFeatureEnabled } from "../../../../../services/flagsService";
import Field from "../Field";
import { fmtDate } from "../utils";
import { RichTextView } from "../../../TournamentPanel/Appeals/shared";

interface EventDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  detailEvent: any | null;
  user: any;
  profile: any;
  ownProfile: boolean;
  registrations: any[];
  isRegistered: boolean;
  registerTeamId: number | null;
  setRegisterTeamId: (id: number | null) => void;
  registrationLoading: boolean;
  onRegister: () => void;
}

const EventDetailDialog: React.FC<EventDetailDialogProps> = ({
  open,
  onOpenChange,
  detailEvent,
  user,
  profile,
  ownProfile,
  registrations,
  isRegistered,
  registerTeamId,
  setRegisterTeamId,
  registrationLoading,
  onRegister,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        {detailEvent && (
          <>
            <DialogHeader>
              <DialogTitle className="text-lg">{detailEvent.name}</DialogTitle>
            </DialogHeader>

            <div className="space-y-3 mt-1">
              <div className="flex flex-col gap-2 text-sm text-gray-600 dark:text-gray-300">
                {detailEvent.date && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-blue-500 flex-shrink-0 dark:text-indigo-300" />
                    {fmtDate(detailEvent.date)}
                  </div>
                )}
                {detailEvent.location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-blue-500 flex-shrink-0 dark:text-indigo-300" />
                    {detailEvent.location}
                  </div>
                )}
                {detailEvent.fee != null && detailEvent.fee > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="w-4 h-4 flex items-center justify-center text-blue-500 font-bold text-xs dark:text-indigo-300">₼</span>
                    {detailEvent.fee} AZN iştirak haqqı
                  </div>
                )}
                {(detailEvent.gameType || detailEvent.registrationType) && (
                  <div className="flex gap-2 flex-wrap mt-1">
                    {detailEvent.gameType && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 dark:bg-blue-950 text-blue-700 text-xs font-semibold dark:text-indigo-300">
                        {detailEvent.gameType === "team" ? (
                          <>
                            <Users className="w-3 h-3" /> Komanda
                          </>
                        ) : (
                          <>
                            <User className="w-3 h-3" /> Fərdi
                          </>
                        )}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {detailEvent.information && (
                <RichTextView
                  className="text-gray-600 dark:text-gray-300 border-t border-gray-100 dark:border-gray-800 pt-3"
                  html={decodeEntities(detailEvent.information)}
                />
              )}

              <Separator />

              {detailEvent.registrationType === "link" && detailEvent.registrationLink ? (
                <a href={safeExternalUrl(detailEvent.registrationLink)} target="_blank" rel="noopener noreferrer" className="w-full">
                  <Button className="w-full gap-2">
                    <ExternalLink className="w-4 h-4" />
                    Qeydiyyat linkə keç
                  </Button>
                </a>
              ) : detailEvent.registrationType === "button" || (!detailEvent.registrationType && !detailEvent.registrationLink) ? (
                <div className="space-y-3">
                  {isRegistered ? (
                    <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-950 rounded-xl text-green-700 text-sm font-semibold">
                      <CheckCircle className="w-4 h-4 flex-shrink-0" />
                      Qeydiyyatdan keçmisiniz
                    </div>
                  ) : user.isLoggedIn ? (
                    <>
                      {detailEvent.gameType === "team" && profile?.teams?.length > 0 && (
                        <Field label="Komanda seçin">
                          <div className="space-y-2">
                            {profile.teams.map((team: any) => (
                              <button
                                key={team.id}
                                type="button"
                                onClick={() => setRegisterTeamId(team.id)}
                                className={cn(
                                  "w-full flex items-center justify-between px-3 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all",
                                  registerTeamId === team.id
                                    ? "border-blue-500 bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-indigo-300"
                                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-700 dark:text-gray-200"
                                )}
                              >
                                <span className="flex items-center gap-2">
                                  <Users className="w-4 h-4" />
                                  {team.name}
                                </span>
                                {registerTeamId === team.id && <Check className="w-4 h-4" />}
                              </button>
                            ))}
                          </div>
                        </Field>
                      )}
                      {detailEvent.gameType === "team" && (!profile?.teams || profile.teams.length === 0) && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                          Bu tədbirə qeydiyyat üçün komandanız olmalıdır.
                        </p>
                      )}
                      <Button
                        onClick={onRegister}
                        disabled={registrationLoading || (detailEvent.gameType === "team" && !registerTeamId)}
                        className="w-full"
                      >
                        {registrationLoading ? "..." : "Qeydiyyatdan keç"}
                      </Button>
                    </>
                  ) : isFeatureEnabled('USER_ACCOUNTS') ? (
                    <AuthLink mode="login" className="block w-full">
                      <Button variant="outline" className="w-full gap-2">
                        <ArrowRight className="w-4 h-4" />
                        Qeydiyyat üçün daxil olun
                      </Button>
                    </AuthLink>
                  ) : null}
                </div>
              ) : detailEvent.registrationLink ? (
                <a href={safeExternalUrl(detailEvent.registrationLink)} target="_blank" rel="noopener noreferrer" className="w-full">
                  <Button className="w-full gap-2">
                    <ExternalLink className="w-4 h-4" />
                    Qeydiyyat
                  </Button>
                </a>
              ) : null}

              {ownProfile && registrations.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">
                      {detailEvent.gameType === "team" ? `Komandalar (${registrations.length})` : `İştİrakçılar (${registrations.length})`}
                    </p>
                    <div className="space-y-1.5 max-h-40 overflow-y-auto">
                      {registrations.map((r: any) => (
                        <div key={r.id} className="flex flex-col gap-0.5 py-1.5 border-b border-gray-100 dark:border-gray-800 last:border-0">
                          <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                            {detailEvent.gameType === "team" ? (
                              <>
                                <Users className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                                <span className="font-medium">{r.team?.name}</span>
                              </>
                            ) : (
                              <>
                                <User className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                                <span className="font-medium">
                                  {r.user?.name} {r.user?.surname}
                                </span>
                              </>
                            )}
                          </div>
                          {/* Team event: show registrant (captain) contact from r.user */}
                          {detailEvent.gameType === "team" && r.user && (
                            <div className="pl-5 space-y-0.5">
                              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                                Qeydiyyatçı: {r.user.name} {r.user.surname}
                              </p>
                              <div className="flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
                                {r.user.phone && (
                                  <a href={`tel:${r.user.phone}`} className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-indigo-200">
                                    📞 {r.user.phone}
                                  </a>
                                )}
                                {r.user.email && (
                                  <a href={`mailto:${r.user.email}`} className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-indigo-200">
                                    ✉️ {r.user.email}
                                  </a>
                                )}
                              </div>
                            </div>
                          )}
                          {/* Individual event: show player's contact */}
                          {detailEvent.gameType !== "team" && (r.user?.phone || r.user?.email) && (
                            <div className="flex flex-wrap gap-3 pl-5 text-xs text-gray-500 dark:text-gray-400">
                              {r.user?.phone && (
                                <a href={`tel:${r.user.phone}`} className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-indigo-200">
                                  📞 {r.user.phone}
                                </a>
                              )}
                              {r.user?.email && (
                                <a href={`mailto:${r.user.email}`} className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-indigo-200">
                                  ✉️ {r.user.email}
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EventDetailDialog;
